/* eslint-disable max-len */
const AppPath = require('@/lib/Navigation/AppPath');
const CurrentRedemptionAction = require('@/lib/Memberships/CurrentRedemptionAction');
const EmbeddedComponents = require('@/vue/EmbeddedComponents');
const EventNames = require('@/lib/Events/EventNames');
const ItemEditableStatus = require('@/lib/Orders/ItemEditableStatus');
const ItemType = require('@/lib/Items/ItemType');
const mapOrderToCart = require('@/libraries/DigiTickets/Mappers/mapOrderToCart');
const PrivilegeNames = require('@/lib/Privileges/PrivilegeNames');
const { cloneDeep } = require('@/functions/clone');
const { container } = require('@/container');
// Used in commented out code block.
// const { getAutomaticRedemptionAvailability } = require('@/lib/Redemption/autoRedemptionFunctions');
const {
    getMembershipsFromOrderLines,
    getOrderLineEditableStatus,
} = require('@/lib/Orders/orderLineFunctions');
const {
    getOrderFromSearchCache,
    invalidateOrderSearchCache,
    invalidateOrderSearchCacheOnRedemption,
} = require('@/lib/Orders/orderSearchCacheFuncs');
// Used in commented out code block.
// const { isDesktop } = require('@/functions/screen');
const { isElementVisible } = require('@/functions/dom');
const { isGroupRedeemableCheck } = require('@/epos/vue/pages/order-details/functions/redemptionFunctions');
const { isSameDay } = require('@/functions/date');
const { MODAL_NAMES__EPOS } = require('@/epos/vue/modals/MODAL_NAMES__EPOS');
const { orderIsCrossBranch } = require('@/functions/orders');
const { suppressModalCancelledErrorOrReThrow } = require('@/lib/Errors');

/**
 * @param $filter
 * @param $location
 * @param $rootScope
 * @param $routeParams
 * @param $scope
 * @param $timeout
 * @param {CartService} cartService
 * @param {ConnectivityChecker} connectivityChecker
 * @param {CurrentDevice} currentDevice
 * @param {DialogService} dialogService
 * @param {GiftVoucherRepository} giftVoucherRepository
 * @param {ItemOptionManager} itemOptionManager
 * @param {KitchenOrderFactory} kitchenOrderFactory
 * @param {LangService} langService
 * @param {MembershipManagementService} membershipManagementService
 * @param {MembershipRedemptionService} membershipRedemptionService
 * @param {MembershipService} membershipService
 * @param {ModalFactory} modalFactory
 * @param {NavigationService} navigationService
 * @param {OrderSearcher} orderSearcher
 * @param {OrderService} orderService
 * @param {PrivilegesManager} privilegesManager
 * @param {SearchContext} searchContext
 * @param {TicketRedeemer} ticketRedeemer
 * @param {TaxManager} taxManager
 * @param {ToastFactory} toastFactory
 * @param {UserService} userService
 * @param {VueModalFactory} vueModalFactory
 * @param {VuexService} vuexService
 */
const OrderDetailCtrl = function (
    $filter,
    $location,
    $rootScope,
    $routeParams,
    $scope,
    $timeout,
    cartService,
    connectivityChecker,
    currentDevice,
    dialogService,
    giftVoucherRepository,
    itemOptionManager,
    kitchenOrderFactory,
    langService,
    membershipManagementService,
    membershipRedemptionService,
    membershipService,
    modalFactory,
    navigationService,
    orderSearcher,
    orderService,
    privilegesManager,
    searchContext,
    ticketRedeemer,
    taxManager,
    toastFactory,
    userService,
    vueModalFactory,
    vuexService
) {
    navigationService.showNav();

    $timeout(() => {
        // Clear any previous order for safety. The new order will be set in setCurrentOrder().
        vuexService.commit('orderDetails/reset');
        vuexService.commit('addActiveEmbeddedComponent', { name: EmbeddedComponents.ORDER_DETAILS_VIEW });
    });

    // The $rootScope.$on listeners will not be automatically cleaned up when the controller closes so we need to
    // handle this cleanup manually.
    // Because there are so many listeners let's keep references to them in an array.
    const rootScopeDeregistrations = [];

    const onRootScope = (eventName, handler) => {
        rootScopeDeregistrations.push(
            $rootScope.$on(eventName, handler)
        );
    };

    $scope.$on('$destroy', () => {
        vuexService.commit('orderDetails/reset');
        vuexService.commit('removeActiveEmbeddedComponent', EmbeddedComponents.ORDER_DETAILS_VIEW);

        // Loop through and remove all the $rootScope listeners.
        rootScopeDeregistrations.forEach((deregister) => deregister());
    });

    onRootScope(EventNames.TICKET_REDEMPTION_ACTIVITY, function () {
        // While using Angular controllers + Vue cloneDeep forces vue to notice deeply nested changes.
        vuexService.commit('orderDetails/setCurrentOrder', cloneDeep($scope.currentOrder));
    });

    onRootScope(EventNames.MEMBERSHIP_REDEMPTION_ACTIVITY, function () {
        // While using Angular controllers + Vue cloneDeep forces vue to notice deeply nested changes.
        vuexService.commit('orderDetails/setCurrentOrder', cloneDeep($scope.currentOrder));
    });

    onRootScope(EventNames.INSTANCE_REDEMPTION_BOX_REDEEM_ITEM_INSTANCE, async function (event, { orderLineID, itemInstanceID }) {
        const orderLine = getOrderLineByID(orderLineID);
        if (!orderLine) {
            console.error('INSTANCE_REDEMPTION_BOX_REDEEM_ITEM_INSTANCE unknown orderLineID: ' + orderLineID);
            return;
        }

        const itemInstance = getItemInstanceByID(orderLine, itemInstanceID);
        if (!itemInstance) {
            console.error('INSTANCE_REDEMPTION_BOX_REDEEM_ITEM_INSTANCE unknown itemInstanceID: ' + itemInstanceID);
            return;
        }
        await $scope.redeemItemInstance(orderLine, itemInstance);
    });

    onRootScope(EventNames.INSTANCE_REDEMPTION_BOX_UNREDEEM_ITEM_INSTANCE_CHECK, async function (event, { orderLineID, itemInstanceID }) {
        const orderLine = getOrderLineByID(orderLineID);
        if (!orderLine) {
            console.error('INSTANCE_REDEMPTION_BOX_UNREDEEM_ITEM_INSTANCE_CHECK unknown orderLineID: ' + orderLineID);
            return;
        }

        const itemInstance = getItemInstanceByID(orderLine, itemInstanceID);
        if (!itemInstance) {
            console.error('INSTANCE_REDEMPTION_BOX_UNREDEEM_ITEM_INSTANCE_CHECK unknown itemInstanceID: ' + itemInstanceID);
            return;
        }
        await $scope.unredeemItemInstanceCheck(orderLine, itemInstance);
    });

    onRootScope(EventNames.LINE_REDEMPTION_BOX_REDEEM_ONE, async function (event, orderLineID) {
        const orderLine = getOrderLineByID(orderLineID);
        if (!orderLine) {
            console.error('LINE_REDEMPTION_BOX_REDEEM_ONE unknown orderLineID: ' + orderLineID);
            return;
        }
        await $scope.redeemOne(orderLine);
    });

    onRootScope(EventNames.LINE_REDEMPTION_BOX_UNREDEEM_ONE, async function (event, orderLineID) {
        const orderLine = getOrderLineByID(orderLineID);
        if (!orderLine) {
            console.error('LINE_REDEMPTION_BOX_UNREDEEM_ONE unknown orderLineID: ' + orderLineID);
            return;
        }
        await $scope.unredeemOne(orderLine);
    });

    onRootScope(EventNames.LINE_REDEMPTION_BOX_UNREDEEM_ALL_ON_LINE_CHECK, async function (event, orderLineID) {
        const orderLine = getOrderLineByID(orderLineID);
        if (!orderLine) {
            console.error('LINE_REDEMPTION_BOX_UNREDEEM_ALL_ON_LINE_CHECK unknown orderLineID: ' + orderLineID);
            return;
        }
        await $scope.unredeemAllOnLineCheck(orderLine);
    });

    onRootScope(EventNames.LINE_REDEMPTION_BOX_REDEEM_ALL_REMAINING_ON_LINE, async function (event, orderLineID) {
        const orderLine = getOrderLineByID(orderLineID);
        if (!orderLine) {
            console.error('LINE_REDEMPTION_BOX_REDEEM_ALL_REMAINING_ON_LINE unknown orderLineID: ' + orderLineID);
            return;
        }
        await $scope.redeemAllRemainingOnLine(orderLine);
    });

    onRootScope(EventNames.ORDER_DETAILS_TAB_CHANGED, function (event, newGroup) {
        const index = $scope.itemGroups.findIndex((group) => group.ID === newGroup.ID);
        if (index === -1) {
            console.error('ORDER_DETAILS_TAB_CHANGED unknown groupID: ' + newGroup.ID);
            return;
        }
        $scope.selectGroup(index);
    });

    onRootScope(EventNames.REDEEM_GROUP_BUTTON_REDEEM_GROUP, async function (event, groupID) {
        const group = $scope.itemGroups.find((g) => g.ID === groupID);
        if (!group) {
            console.error('REDEEM_GROUP_BUTTON_REDEEM_GROUP unknown groupID: ' + groupID);
            return;
        }
        await $scope.redeemGroup(group);
    });

    onRootScope(EventNames.REDEEM_GROUP_BUTTON_UNREDEEM_GROUP, async function (event, groupID) {
        const group = $scope.itemGroups.find((g) => g.ID === groupID);
        if (!group) {
            console.error('REDEEM_GROUP_BUTTON_UNREDEEM_GROUP unknown groupID: ' + groupID);
            return;
        }
        await $scope.unredeemGroup(group);
    });

    onRootScope(EventNames.REDEEM_ORDER_BUTTON_REDEEM_ORDER, async function (event, groupIDs) {
        const groups = groupIDs
            .map((groupID) => $scope.itemGroups.find((g) => g.ID === groupID))
            .filter(Boolean);

        if (groups.length !== groupIDs.length) {
            const missingIDs = groupIDs.filter((groupID) => !groups.some((group) => group.ID === groupID));
            console.error('REDEEM_ORDER_BUTTON_REDEEM_ORDER unknown groupIDs: ' + missingIDs.join(', '));
            return;
        }

        // Using a for loop so these group redemptions can happen one at a time to deal with things like
        // cross-branch and unallocated-seat confirmation modals.
        // TODO: needs a bit of serious testing.
        for (const group of groups) {
            await $scope.redeemGroup(group);
        }
    });

    onRootScope(EventNames.ORDER_DETAILS_MEMBERSHIP_LOG_VISIT, async function (event, { orderLineID, itemInstanceID }) {
        const orderLine = getOrderLineByID(orderLineID);
        if (!orderLine) {
            console.error('ORDER_DETAILS_MEMBERSHIP_LOG_VISIT unknown orderLineID: ' + orderLineID);
            return;
        }

        const itemInstance = getItemInstanceByID(orderLine, itemInstanceID);
        if (!itemInstance) {
            console.error('ORDER_DETAILS_MEMBERSHIP_LOG_VISIT unknown itemInstanceID: ' + itemInstanceID);
            return;
        }

        await $scope.logVisit(
            itemInstance.getMembership(),
            itemInstance.membershipSubscription,
            orderLine.item
        );
    });

    onRootScope(EventNames.ORDER_DETAILS_MEMBERSHIP_UNLOG_VISIT, async function (event, { orderLineID, itemInstanceID }) {
        const orderLine = getOrderLineByID(orderLineID);
        if (!orderLine) {
            console.error('ORDER_DETAILS_MEMBERSHIP_UNLOG_VISIT unknown orderLineID: ' + orderLineID);
            return;
        }

        const itemInstance = getItemInstanceByID(orderLine, itemInstanceID);
        if (!itemInstance) {
            console.error('ORDER_DETAILS_MEMBERSHIP_UNLOG_VISIT unknown itemInstanceID: ' + itemInstanceID);
            return;
        }

        await $scope.unlogVisit(
            itemInstance.getMembership(),
            itemInstance.membershipSubscription,
            orderLine.item
        );
    });

    onRootScope(EventNames.ORDER_DETAILS_MEMBERSHIP_EDIT_MEMBERSHIP, async function (event, { orderLineID, itemInstanceID }) {
        const orderLine = getOrderLineByID(orderLineID);
        if (!orderLine) {
            console.error('ORDER_DETAILS_MEMBERSHIP_EDIT_MEMBERSHIP unknown orderLineID: ' + orderLineID);
            return;
        }

        const itemInstance = getItemInstanceByID(orderLine, itemInstanceID);
        if (!itemInstance) {
            console.error('ORDER_DETAILS_MEMBERSHIP_EDIT_MEMBERSHIP unknown itemInstanceID: ' + itemInstanceID);
            return;
        }

        await $scope.editMembership(
            itemInstance.getMembership(),
            orderLine.item
        );
    });

    onRootScope(EventNames.ORDER_DETAILS_MEMBERSHIP_TOGGLE_MEMBER_VISIT, async function (event, { memberID, orderLineID, itemInstanceID }) {
        const orderLine = getOrderLineByID(orderLineID);
        if (!orderLine) {
            console.error('ORDER_DETAILS_MEMBERSHIP_TOGGLE_MEMBER_VISIT unknown orderLineID: ' + orderLineID);
            return;
        }

        const itemInstance = getItemInstanceByID(orderLine, itemInstanceID);
        if (!itemInstance) {
            console.error('ORDER_DETAILS_MEMBERSHIP_TOGGLE_MEMBER_VISIT unknown itemInstanceID: ' + itemInstanceID);
            return;
        }

        const member = itemInstance.getMembership().getMemberByID(memberID);
        if (!member) {
            console.error('ORDER_DETAILS_MEMBERSHIP_TOGGLE_MEMBER_VISIT unknown memberID: ' + memberID);
            return;
        }

        await $scope.toggleMemberVisit(
            member,
            itemInstance.membershipSubscription,
            orderLine.item,
            itemInstance.getMembership(),
        );
    });

    onRootScope(EventNames.ORDER_DETAILS_MEMBERSHIP_CLEAR_RENEWING_AND_VIEW_MEMBERSHIP, async function (event, { ref, groupID }) {
        const group = $scope.itemGroups.find((g) => g.ID === groupID);
        if (!group) {
            console.error('ORDER_DETAILS_MEMBERSHIP_CLEAR_RENEWING_AND_VIEW_MEMBERSHIP unknown groupID: ' + groupID);
            return;
        }
        await $scope.clearRenewingAndViewMembership(ref, group);
    });

    $scope.currentBranch = userService.currentBranch;

    /**
     * $filter and $routeParams are used in the template.
     */
    $scope.$filter = $filter;
    $scope.$routeParams = $routeParams;

    $scope.showLoader = true;
    $scope.isRefreshing = false;
    $scope.showNotFound = false;

    /**
     * @type {?{text: string, success: boolean}}
     */
    // $scope.autoRedemptionResult = null;

    /**
     * @type {Order}
     */
    $scope.currentOrder = null;

    /**
     * We'll show branch names on items, and in the details on the left, if the order is from or contains any items
     * from a branch other than the current one.
     *
     * @type {boolean}
     */
    $scope.showBranchNames = false;

    /**
     * This is for offline support of orders with waivers. It is determined by looking at the items in the order
     * and checking our cached tickets to see if those tickets would require a waiver.
     * The best way to determine if an order has waivers is to simply load waivers for the order. But we can't do that
     * while offline so this is a backup.
     *
     * @type {boolean|null}
     */
    $scope.orderContainsTicketsWithWaivers = null;

    /**
     * This service is called from templates.
     *
     * @type {MembershipManagementService}
     */
    $scope.membershipManagementService = membershipManagementService;

    $scope.isOnline = connectivityChecker.online;
    connectivityChecker.addObserver({
        key: 'OrderDetailCtrl',
        notifyOnline() {
            $timeout(() => {
                $scope.isOnline = true;
            });
        },
        notifyOffline() {
            $timeout(() => {
                $scope.isOnline = false;
            });
        },
    });

    $scope.device = currentDevice.device;

    const getOrderLineByID = (orderLineID) => {
        return $scope.currentOrder.items.find((line) => line.ID === orderLineID);
    };

    const getItemInstanceByID = (orderLine, itemInstanceID) => {
        return orderLine.iteminstances.find((instance) => instance.ID === itemInstanceID);
    };

    /**
     * @param {Order} order
     * @param {boolean} [wasBarcodeScan]
     */
    const setCurrentOrder = async (order, wasBarcodeScan) => {
        try {
            await taxManager.addBookingTaxToOrder(order);
        } catch (err) {
            // There was an error loading the tax summary for the order.
            // A VAT receipt will not be available.
            console.error('Failed to load tax for order.', err);
            vuexService.commit('orderDetails/setTaxLoadError', err);
        }

        try {
            await itemOptionManager.attachOptionSetsToOrderLines(order.items);
        } catch (err) {
            console.warn('Failed to attach item option sets to order lines.', err);
        }

        if (container.eInvoicingService.inUseForCompany()) {
            // If a greek tax invoice was created for this order there will be a custom field called
            // INVOICE_URL containing a URL to the invoice. Set this as the order's receipt QR code URL
            // so it appears on a reprint of the receipt.
            const invoiceUrl = container.eInvoicingService.getInvoiceUrlFromOrder(order);
            if (invoiceUrl) {
                order.addReceiptQRCodeUrl(invoiceUrl);
            }
            const creditNoteUrl = container.eInvoicingService.getCreditNoteUrlFromOrder(order);
            if (creditNoteUrl) {
                order.addReceiptQRCodeUrl(creditNoteUrl);
            }
        }

        $timeout(() => {
            $scope.currentOrder = order;

            // Commit and then read itemGroups back so we only have to build them once and can then use the same data
            // in both Angular and Vue.
            vuexService.commit('orderDetails/setCurrentOrder', order);
            $scope.itemGroups = vuexService.getter('orderDetails/itemGroups');

            const isCrossBranch = orderIsCrossBranch(userService.getOrderBranchID(), order, order.getLines());
            $scope.showBranchNames = isCrossBranch;
            vuexService.commit('orderDetails/setCurrentOrderIsCrossBranch', isCrossBranch);
            vuexService.commit('orderDetails/setCurrentOrderIsFromBarcodeScan', wasBarcodeScan);

            if (wasBarcodeScan) {
                // After PROP-1214-prefer-loading-orders-online-from-the-order-details-page (released Feb 2024)
                // this block never ran because wasBarcodeScan was always false. This was deployed on 21st Feb 2024
                // but there haven't been any complaints probably because the auto redemption rules are a bit tight.
                // As of PROP-1700-make-outstanding-balances-more (Feb 2025) wasBarcodeScan can now be true or false
                // again, but we will leave this block disabled anyway.
                // if ($scope.checkAutoRedeem()) {
                //      Auto redemption happened.
                //      Send the user back to the search page, so they can scan some more.
                //      PROP-347 this might not be the intended behaviour as you can stay on
                //      the page and scan more barcodes anyway.
                //      $timeout(() => {
                //          window.history.go(-1);
                //      }, 2000);
                // }
            }

            $scope.selectDefaultGroup();

            // To make sure the receipt is marked as a duplicate.
            // When printing from the order details page we will always mark is as a duplicate as it is assumed a
            // receipt was printed when it was sold, and it doesn't really matter.
            // This line purposefully comes before the Greek Tax handling so that will never trigger a new tax receipt
            // when printing from the order details page.
            order.receiptPrinted = true;

            $scope.showLoader = false;

            guessIfOrderLinesHaveWaivers()
                .then(() => {
                    $timeout(() => {});
                });
        });

        $timeout(() => {
            // If we have searched for a ticket instance, and it is selected then scroll to it.
            // We have to add a short pause to give the angular template time to update and the element to actually be
            // in the dom, and we can't just use scrollIntoView() because we need an offset.
            const el = document.querySelector('.instance-redemption-box__selected');
            const target = document.querySelector('#epos-screen');
            if (el && !isElementVisible(el, target)) {
                const currentPosition = el.getBoundingClientRect().top;
                const offsetPosition = currentPosition + window.scrollY - 45; // 45px offset.
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth',
                });
            }
        }, 500);
    };

    /**
     * @param {string} ref
     * @param {boolean} wasBarcodeScan
     */
    const loadOrderByRef = async (ref, wasBarcodeScan = false) => {
        // This function is sometimes called to change the order shown, rather than a full page reload.
        // We want this to have the same effect for toast clearing purposes.
        toastFactory.clearToastsOnNavigation();

        $scope.currentGroup = null;
        $scope.currentGroupIsForSessionToday = null;
        $scope.currentGroupRedeemAllButtonColorClass = null;

        $scope.showNotFound = false;
        $scope.showLoader = true;
        $scope.currentOrder = null;

        // Try to load the latest version of the order from the API.
        try {
            const remoteOrder = await orderSearcher.remoteGet(ref);
            console.debug('Loaded order from API.', remoteOrder);
            await setCurrentOrder(remoteOrder, wasBarcodeScan);
            return;
        } catch (e) {

        }

        // Try to load the order from recent search results.
        const cachedOrder = getOrderFromSearchCache(ref);
        if (cachedOrder) {
            console.debug('Loaded order from search cache.', cachedOrder);
            await setCurrentOrder(cachedOrder, wasBarcodeScan);
            return;
        }

        // Try to load the order from the local downloaded orders database.
        try {
            const localDatabaseOrder = await orderSearcher.localGet(ref);
            console.debug('Loaded order from local database.', localDatabaseOrder);
            await setCurrentOrder(localDatabaseOrder, wasBarcodeScan);
            return;
        } catch (e) {

        }

        $timeout(() => {
            $scope.showNotFound = true;
            $scope.showLoader = false;
        });
    };

    // Process the bar code if scanned.
    $scope.$on(EventNames.BARCODE_SCANNED, function (event, response) {
        console.log('Barcode Scanned (Order Detail)');
        let barcode = response.code;
        if (barcode.length > 0) {
            loadOrderByRef(barcode, true);
        }
    });

    $scope.$on(EventNames.CUSTOMER_ADDED_TO_ORDER, function (event, customer) {
        $scope.currentOrder.customer = customer;
    });

    const onOrderStateChanged = () => {
        // Any cached search results will still show this order's old state.
        invalidateOrderSearchCache();

        $scope.refreshCurrentOrder();
    };

    $scope.$on(EventNames.ORDER_CONFIRMED, onOrderStateChanged);
    $scope.$on(EventNames.ORDER_VOIDED, onOrderStateChanged);

    /**
     * Makes a guess if each ticket line in th current order *should* contain waivers, based on the last synced
     * information about tickets.
     * It looks at the tickets that are in the order and checks if any of those tickets are set to require a waiver.
     * There is a possibility this doesn't match up because the synced data is out of data and can give both false
     * positives and false negatives. This is only currently used when the device is offline so that chance
     * is acceptably low.
     * To tell for certain if the order has waivers make a request to the waivers API to ask.
     *
     * @return {Promise<void>}
     */
    const guessIfOrderLinesHaveWaivers = async () => {
        $scope.orderContainsTicketsWithWaivers = false;

        if (!$scope.currentOrder) {
            return;
        }

        let ticketCache = {};
        for (let i = 0; i < $scope.currentOrder.items.length; i++) {
            const orderLine = $scope.currentOrder.items[i];
            if (orderLine.itemType !== ItemType.TICKET) {
                continue;
            }
            try {
                if (!ticketCache.hasOwnProperty(orderLine.itemID)) {
                    ticketCache[orderLine.itemID] = await container.ticketRepository.find(orderLine.itemID);
                }
                orderLine.ticketHasWaiver = ticketCache[orderLine.itemID].hasWaiver;
                if (orderLine.ticketHasWaiver) {
                    $scope.orderContainsTicketsWithWaivers = true;
                }
            } catch (e) {
                orderLine.ticketHasWaiver = false;
            }
        }
    };

    /**
     * Is the edit order button visible?
     *
     * The button should only be displayed for companies who have order editing enabled in the Back Office.
     * When the button is clicked the edit_orders permission applies.
     *
     * @type {boolean}
     */
    $scope.showEditBtn = userService.currentUser.company.enableOrderEditing;

    $scope.refreshCurrentOrder = () => {
        if (!$scope.currentOrder) {
            return;
        }

        $scope.isRefreshing = true;

        refreshOrder($scope.currentOrder)
            .then((refreshedOrder) => setCurrentOrder(refreshedOrder, false))
            .finally(() => {
                $timeout(() => {
                    $scope.isRefreshing = false;
                });
            });
    };

    /**
     * @param {Order} order
     *
     * @return {Promise<Order>}
     */
    const refreshOrder = async (order) => {
        try {
            return await orderSearcher.remoteGet(order.ID);
        } catch (e) {
            toastFactory.errorTop(
                'ORDER_DETAILS.NOT_FOUND',
                'There was a problem fetching the latest version of this order. Please ensure you are online and the order has not been cancelled.'
            );
            throw e;
        }
    };

    /**
     * @param {Order} order
     */
    $scope.edit = async function (order) {
        // If a refund cannot be processed, alter the user at this point.
        if (container.eInvoicingService.inUseForCompany() && order.nonZeroCreditNoteHasBeenIssued()) {
            const confirmed = await dialogService.confirm('ORDER_DETAILS.REFUND_CREDIT_NOTE_ALREADY_ISSUED');
            if (!confirmed) {
                return;
            }
        }

        privilegesManager
            .requirePrivilege(PrivilegeNames.EDIT_ORDERS)
            .then(() => {
                doEdit(order);
            });
    };

    const doEdit = async (order, balancePaymentAmount = null) => {
        // Load a fresh copy of the order from the API.
        const freshOrder = await refreshOrder(order);

        // Retrieve information on the vouchers in the order.
        for (let i = 0; i < freshOrder.getLines().length; i++) {
            const line = freshOrder.getLines()[i];
            if (line.getItemType() === ItemType.GIFT_VOUCHER) {
                const trueGiftVoucher = await giftVoucherRepository.find(line.item.ID);
                line.item.expiresAfter = trueGiftVoucher.expiresAfter;
                line.item.expiresAfterUnit = trueGiftVoucher.expiresAfterUnit;
            }
        }

        let cart = await mapOrderToCart(freshOrder);

        // Setup where to send the user after it has been modified.
        cart.sellState.cancelCartReturnPath = new AppPath('/orders/' + freshOrder.bookingRef + '/false');

        // Don't set a successReturnPath because there is already the option to View Order
        // in the change modal. Setting a successReturnPath would cause only an OK button to be shown
        // and the user forced back to the order details page.
        // We can add query params using the viewOrderButtonPathQuery.

        cart.isEditingOrder = true;
        let returnAdjustmentStatus = 'adjusted';

        if (balancePaymentAmount) {
            // Calling this automatically sets payNow to true.
            cart.sellState.setBalancePaymentAmount(balancePaymentAmount);
            returnAdjustmentStatus = 'balance-paid';
        }

        if (order.fulfillmentCreatedAt) {
            // TODO: Should this be moved to mapOrderToCart?
            cart.restoredKitchenOrder = await kitchenOrderFactory.map(freshOrder);
        }

        cart.sellState.viewOrderButtonPathQuery = { 'adjustment-status': returnAdjustmentStatus };

        const cartSet = await cartService.setNewCart(cart, false);
        if (cartSet) {
            navigationService.viewSell();
        }
    };

    /**
     * @type {OrderItemGroup[]}
     */
    $scope.itemGroups = [];

    /**
     * @type {?OrderItemGroup}
     */
    $scope.currentGroup = null;

    /**
     * Redeem every redeemable item in the given group.
     *
     * @param {OrderItemGroup} group
     */
    $scope.redeemGroup = async (group) => {
        const linesToRedeem = group.items.filter(
            (line) => $scope.redemptionEnabledForOrderLine(line)
        );

        if (await ticketRedeemer.isUnconfirmedCrossBranchRedemption(linesToRedeem)) {
            return;
        }

        if (await ticketRedeemer.isUnconfirmedUnallocatedSeatRedemption(linesToRedeem)) {
            return;
        }

        if (group.itemType === ItemType.MEMBERSHIP_PLAN) {
            redeemMemberships(linesToRedeem);
        }

        if (group.itemType === ItemType.TICKET) {
            invalidateOrderSearchCacheOnRedemption();
            ticketRedeemer.redeemLines(
                $scope.currentOrder,
                linesToRedeem
            );
        }
    };

    /**
     * Unredeem every redeemed item in the given group. If the operator does not have permission prompt for approval.
     *
     * @param {OrderItemGroup} group
     */
    $scope.unredeemGroup = (group) => {
        const linesToUnredeem = group.items.filter(
            (line) => $scope.redemptionEnabledForOrderLine(line)
        );

        if (group.itemType === ItemType.MEMBERSHIP_PLAN) {
            privilegesManager
                .requirePrivilege(PrivilegeNames.ALLOW_UNREDEEM_MEMBERSHIPS)
                .then(() => {
                    unredeemMemberships(linesToUnredeem);
                });
        }

        if (group.itemType === ItemType.TICKET) {
            privilegesManager
                .requirePrivilege(PrivilegeNames.ALLOW_UNREDEEM_TICKETS)
                .then(async () => {
                    if (await ticketRedeemer.isUnconfirmedCrossBranchRedemption(linesToUnredeem, true)) {
                        return;
                    }

                    // Don't check seat allocations for unredemption.

                    invalidateOrderSearchCacheOnRedemption();
                    ticketRedeemer.unredeemLines($scope.currentOrder, linesToUnredeem);
                });
        }
    };
    /**
     * @param {OrderLine[]} orderLines
     */
    const redeemMemberships = (orderLines) => {
        getMembershipsFromOrderLines(orderLines)
            .forEach(({ membership, membershipPlan, membershipSubscription }) => {
                if (membership.hasNonRedeemedMembersToday()) {
                    if (membership.currentRedemptionAction === CurrentRedemptionAction.NONE) {
                        membership.currentRedemptionAction = CurrentRedemptionAction.REDEEMING;
                        $scope.logVisit(
                            membership,
                            membershipSubscription,
                            membershipPlan,
                        );
                    } else {
                        console.log('OrderDetailCtrl.redeemMemberships() skip logVisit redemption action in progress.');
                    }
                }
            });
    };

    /**
     * @param {OrderLine[]} orderLines
     */
    const unredeemMemberships = (orderLines) => {
        getMembershipsFromOrderLines(orderLines)
            .forEach(({ membership, membershipPlan, membershipSubscription }) => {
                if (membership.hasRedeemedMembersToday()) {
                    if (membership.currentRedemptionAction === CurrentRedemptionAction.NONE) {
                        membership.currentRedemptionAction = CurrentRedemptionAction.UNREDEEMING;
                        $scope.unlogVisit(
                            membership,
                            membershipSubscription,
                            membershipPlan
                        );
                    } else {
                        console.log('OrderDetailCtrl.unredeemMemberships() skip logVisit redemption action in progress.');
                    }
                }
            });
    };

    /**
     * Redeem all the remaining possible redemptions of a single line.
     * (line qty - already redeemed qty)
     *
     * @param {OrderLine} orderLine
     * @param {string} [method] Manual or Automatic redemption?
     */
    $scope.redeemAllRemainingOnLine = async (orderLine, method) => {
        if (orderLine.redemptionIsInProgress()) {
            return;
        }

        if (await ticketRedeemer.isUnconfirmedCrossBranchRedemption([orderLine])) {
            return;
        }

        if (await ticketRedeemer.isUnconfirmedUnallocatedSeatRedemption([orderLine])) {
            return;
        }

        invalidateOrderSearchCacheOnRedemption();
        ticketRedeemer.redeemAllRemainingOnLine(
            $scope.currentOrder,
            orderLine,
            method
        );
    };

    /**
     * Unredeem the line. If the operator does not have permission then prompt for supervisor approval.
     *
     * @param {OrderLine} orderLine
     */
    $scope.unredeemAllOnLineCheck = (orderLine) => {
        if (orderLine.redemptionIsInProgress()) {
            return;
        }

        privilegesManager
            .requirePrivilege(PrivilegeNames.ALLOW_UNREDEEM_TICKETS)
            .then(async () => {
                if (await ticketRedeemer.isUnconfirmedCrossBranchRedemption([orderLine], true)) {
                    return;
                }

                // Don't check seat allocations for unredemption.

                invalidateOrderSearchCacheOnRedemption();
                ticketRedeemer.unredeemAllOnLine($scope.currentOrder, orderLine);
            });
    };

    /**
     * Redeem one instance on a line.
     *
     * @param {OrderLine} orderLine
     */
    $scope.redeemOne = async (orderLine) => {
        if (orderLine.redemptionIsInProgress()) {
            return;
        }

        if (await ticketRedeemer.isUnconfirmedCrossBranchRedemption([orderLine])) {
            return;
        }

        if (await ticketRedeemer.isUnconfirmedUnallocatedSeatRedemption([orderLine])) {
            return;
        }

        invalidateOrderSearchCacheOnRedemption();
        ticketRedeemer.redeemQtyOnLine(
            $scope.currentOrder,
            orderLine,
            1
        );
    };

    /**
     * Unredeem one instance on a line.
     *
     * @param {OrderLine} orderLine
     */
    $scope.unredeemOne = (orderLine) => {
        if (orderLine.redemptionIsInProgress()) {
            return;
        }

        privilegesManager
            .requirePrivilege(PrivilegeNames.ALLOW_UNREDEEM_TICKETS)
            .then(async () => {
                if (await ticketRedeemer.isUnconfirmedCrossBranchRedemption([orderLine], true)) {
                    return;
                }

                // Don't check seat allocations for unredemption.

                orderLine.setUnredemptionInProgress();

                invalidateOrderSearchCacheOnRedemption();
                ticketRedeemer.redeemQtyOnLine(
                    $scope.currentOrder,
                    orderLine,
                    -1
                );
            })
            .catch(() => {
                orderLine.clearRedemptionInProgress();
            });
    };

    $scope.selectDefaultGroup = function selectDefaultGroup() {
        $scope.selectGroup(0);
    };

    $scope.selectGroup = function selectGroup(index) {
        // An order could have no lines (therefore no groups) in the case of an adjusted or cancelled order.
        $scope.currentGroup = $scope.itemGroups[index] || null;
        vuexService.commit('orderDetails/setCurrentTab', $scope.currentGroup?.itemType || null);

        // When changing group, cache these values as they won't change so don't need constant function calls.
        $scope.currentGroupIsForSessionToday = $scope.isCurrentGroupForSessionToday();
        $scope.updateRedeemGroupButtonColor();
    };

    $scope.updateRedeemGroupButtonColor = () => {
        $scope.currentGroupRedeemAllButtonColorClass = $scope.getCurrentGroupRedeemAllButtonColorClass();
    };

    $scope.showPayments = function showPayments() {
        vueModalFactory.openAndIgnoreResult(
            MODAL_NAMES__EPOS.ORDER_PAYMENTS,
            {
                payments: $scope.currentOrder.payments,
                orderTotal: $scope.currentOrder.total,
            }
        );
    };

    /**
     * Take the remaining balance for an order, or refund an overpayment for an order.
     * A num pad is displayed for the user to enter the amount to pay or refund. The amount is always entered
     * as a positive number
     *
     * @param {boolean} isRefund Controls which mode it it in, true = refunding an overpayment, false = paying balance.
     */
    $scope.payBalance = function (isRefund = false) {
        // Modal title.
        let title = isRefund ? 'PAY_BALANCE_MODAL.MODAL_TITLE--REFUND' : 'PAY_BALANCE_MODAL.MODAL_TITLE--PAYMENT';

        // Text to display to the user.
        const textLangKeys = [];
        if (isRefund) {
            if ($scope.currentOrder.remainingBalance) {
                textLangKeys.push('PAY_BALANCE_MODAL.OVERPAID');
            }
            textLangKeys.push('PAY_BALANCE_MODAL.ENTER_AMOUNT--REFUND');
        } else {
            if ($scope.currentOrder.remainingBalance) {
                textLangKeys.push('PAY_BALANCE_MODAL.OUTSTANDING');
            }
            textLangKeys.push('PAY_BALANCE_MODAL.ENTER_AMOUNT--PAYMENT');
        }

        const okLabel = isRefund ? 'PAY_BALANCE_MODAL.OK_BTN--REFUND' : 'PAY_BALANCE_MODAL.OK_BTN--PAYMENT';
        const okColor = isRefund ? 'warning' : 'primary';

        let customHtml = null;
        if ($scope.currentOrder.hasPendingPayments) {
            customHtml = '<div class="alert alert-warning alert--small">'
                + '<i class="fa-solid fa-triangle-exclamation"></i>'
                + ' '
                + langService.getText('ORDER_DETAILS.PAY_BALANCE_PENDING_WARNING')
                + '</div>';
        }

        dialogService
            .numPad(
                textLangKeys,
                {
                    title,
                    okLabel,
                    okColor,
                    initialValue: Math.abs($scope.currentOrder.remainingBalance),
                    min: 0.01,
                    max: Math.abs($scope.currentOrder.remainingBalance),
                    numPadOptions: {
                        showSignButton: false,
                        showValueAsCurrency: true,
                    },
                    customHtml,
                }
            )
            .then(({ value }) => {
                if (value !== 0) {
                    if (isRefund) {
                        // If this a refund convert the number to a negative number.
                        value *= -1;
                    }

                    doEdit($scope.currentOrder, value);
                }
            }).catch(suppressModalCancelledErrorOrReThrow);
    };

    $scope.$on(EventNames.PAY_ORDER_BALANCE_REQUESTED, (e, data) => {
        $scope.payBalance(
            data.isRefund
        );
    });

    /**
     * This will be called when arriving on the order details page by scanning an order's booking ref.
     *
     * Check if the current order should be automatically redeemed. Redeem if it should.
     *
     * @return {boolean} True if auto redemption happened.
     */
    // $scope.checkAutoRedeem = () => {
    //     let order = $scope.currentOrder;
    //     let autoRedeemProfile = userService.currentUser.company.getAutoRedeemProfile();
    //     if (!autoRedeemProfile) {
    //         return false;
    //     }
    //
    //     const linesToAutoRedeem = getAutomaticRedemptionAvailability(
    //         order.items,
    //         autoRedeemProfile,
    //         userService.currentBranch
    //     );
    //     if (linesToAutoRedeem.error) {
    //         $scope.autoRedemptionResult = {
    //             success: false,
    //             text: langService.getText('ORDER_AUTO_REDEMPTION.NOT_AUTO_REDEEMED_MSG') + ' ' + langService.getText('ORDER_AUTO_REDEMPTION.' + linesToAutoRedeem.error),
    //         };
    //
    //         if (isDesktop()) {
    //             toastFactory.warning(
    //                 'ORDER_AUTO_REDEMPTION.NOT_AUTO_REDEEMED',
    //                 $scope.autoRedemptionResult.text,
    //                 {
    //                     clearOnNavigation: true,
    //                     timeOut: 10000,
    //                 }
    //             );
    //         }
    //
    //         return false;
    //     }
    //
    //     if (linesToAutoRedeem.lines.length) {
    //         linesToAutoRedeem.lines.forEach((line) => {
    //             if (line.hasIndividualTicketInstances()) {
    //                 line.iteminstances.forEach((itemInstance) => {
    //                     ticketRedeemer.redeemItemInstance($scope.currentOrder, itemInstance);
    //                 });
    //             } else {
    //                 ticketRedeemer.redeemAllRemainingOnLine(
    //                     $scope.currentOrder,
    //                     line,
    //                     'Automatic'
    //                 );
    //             }
    //
    //             invalidateOrderSearchCacheOnRedemption();
    //         });
    //
    //         $scope.autoRedemptionResult = {
    //             success: true,
    //             text: langService.getText('ORDER_AUTO_REDEMPTION.AUTO_REDEEMED_MSG'),
    //         };
    //
    //         if (isDesktop()) {
    //             toastFactory.success(
    //                 'ORDER_AUTO_REDEMPTION.AUTO_REDEEMED',
    //                 $scope.autoRedemptionResult.text,
    //                 {
    //                     clearOnNavigation: true,
    //                     timeOut: 10000,
    //                 }
    //             );
    //         }
    //
    //         return true;
    //     }
    //
    //     return false;
    // };

    /**
     * @param {OrderLine} orderLine
     * @return {boolean}
     */
    $scope.orderLineHasValidFields = (orderLine) => {
        for (let i = 0; i < orderLine.iteminstances.length; i++) {
            for (let j = 0; j < orderLine.iteminstances[i].fields.instances.length; j++) {
                const fieldInstance = orderLine.iteminstances[i].fields.instances[j];
                if (fieldInstance.hasValue && !fieldInstance.error) {
                    return true;
                }
            }
        }

        return false;
    };

    /**
     * @param {OrderLine} orderLine
     * @param {ItemInstance} itemInstance
     */
    $scope.redeemItemInstance = async (orderLine, itemInstance) => {
        if (orderLine.redemptionIsInProgress() || itemInstance.redemptionIsInProgress()) {
            return;
        }

        if (await ticketRedeemer.isUnconfirmedCrossBranchRedemption([orderLine], false, itemInstance)) {
            return;
        }

        if (await ticketRedeemer.isUnconfirmedUnallocatedSeatRedemption([orderLine])) {
            return;
        }

        invalidateOrderSearchCacheOnRedemption();
        ticketRedeemer.redeemItemInstance($scope.currentOrder, itemInstance);
    };

    /**
     * @param {OrderLine} orderLine
     * @param {ItemInstance} itemInstance
     */
    $scope.unredeemItemInstanceCheck = async (orderLine, itemInstance) => {
        if (orderLine.redemptionIsInProgress() || itemInstance.redemptionIsInProgress()) {
            return;
        }

        if (await ticketRedeemer.isUnconfirmedCrossBranchRedemption([orderLine], true, itemInstance)) {
            return;
        }

        // Don't check seat allocations for unredemption.

        privilegesManager
            .requirePrivilege(PrivilegeNames.ALLOW_UNREDEEM_TICKETS)
            .then(() => {
                invalidateOrderSearchCacheOnRedemption();
                ticketRedeemer.unredeemItemInstance($scope.currentOrder, itemInstance);
            })
            .catch((e) => {
                console.error(e);
                itemInstance.clearRedemptionInProgress();
            });
    };

    $scope.isMembershipGroup = function isMembershipGroup() {
        return $scope.currentGroup && $scope.currentGroup.itemType === ItemType.MEMBERSHIP_PLAN;
    };

    /**
     * Is the current selected item group (tab) redeemable?
     *
     * @return {boolean}
     */
    $scope.isGroupRedeemable = function isGroupRedeemable() {
        const isOrderLive = $scope.currentOrder && $scope.currentOrder.isLive();
        return isGroupRedeemableCheck($scope.currentGroup, isOrderLive);
    };

    /**
     * Returns the class to set the colour of the "redeem all" button for the whole group/tab.
     * - red/danger = all lines are already fully redeemed
     * - orange/warning = at least one line has some more redemptions available, but the session is not today
     * - green/success = at least one line has some more redemptions available
     *
     * @see OrderLine.getRedeemButtonColorClass
     * for the same function for each line.
     *
     * @return {string}
     */
    $scope.getCurrentGroupRedeemAllButtonColorClass = () => {
        if (!$scope.isGroupRedeemable()) {
            // Nothing is left redeemable, so this will be an unredemption. Show a red button.
            return 'btn-danger';
        }

        // Otherwise, the button will redeem at least some lines when clicked.

        // Use the pre-cached value for this.
        // This means $scope.currentGroupIsForSessionToday must be set before calling this function.
        if ($scope.currentGroupIsForSessionToday === false) {
            return 'btn-warning';
        }

        return 'btn-success';
    };

    $scope.isCurrentGroupForSessionToday = () => {
        if ($scope.currentGroup && $scope.currentGroup.items[0].session) {
            return isSameDay(
                $scope.currentGroup.items[0].session.startTime,
                new Date()
            );
        }

        // Not a session-based group.
        return null;
    };

    /**
     * @return {boolean}
     */
    $scope.isRedeemCurrentGroupDisabled = () => {
        // If any of the lines in the current group cannot be redeemed (probably due to waivers), return false.
        for (let i = 0; i < $scope.currentGroup.items.length; i++) {
            if (!$scope.redemptionEnabledForOrderLine($scope.currentGroup.items[i])) {
                // Redemption is not allowed for the current line, so *disable* the button:
                return true;
            }
        }

        return !!$scope.currentGroup.items.find((orderLine) => {
            return orderLine.redemptionIsInProgress()
                || orderLine.redemptionError
                || !!orderLine.iteminstances.find((itemInstance) => {
                    return itemInstance.redemptionIsInProgress() || itemInstance.redemptionError;
                });
        }) || $scope.currentGroup.items.every((orderLine) => orderLine.qty <= 0); // Every line is a refund.
    };

    /**
     * Should the "redeem all" button for the current group/tab do a redemption or an unredemption.
     * If there are any unredeemed tickets it will show 'Redeem All Available'.
     * If all tickets are redeemed it will show 'Unredeem All Available'.
     *
     * @return {boolean|null} true = redemption, false = unredemption, null = don't show the button.
     */
    $scope.currentGroupRedeemAllButtonIsRedemption = () => {
        if ($scope.currentGroup.itemType === ItemType.MEMBERSHIP_PLAN) {
            // If there are any members on any memberships in the group that
            // have not been redeemed today it will be a redemption.

            let hasRedeemedMembers = false;

            for (let i = 0; i < $scope.currentGroup.items.length; i++) {
                let orderLine = $scope.currentGroup.items[i];
                for (let j = 0; j < orderLine.iteminstances.length; j++) {
                    let itemInstance = orderLine.iteminstances[j];
                    if (itemInstance.membership) {
                        if (itemInstance.membership.hasNonRedeemedMembersToday() && !orderLine.isExpired()) {
                            return true;
                        }
                        if (itemInstance.membership.hasRedeemedMembersToday()) {
                            hasRedeemedMembers = true;
                        }
                    }
                }
            }

            // If we get this far there are no remaining not-redeemed members.
            // If there are some redeemed we'll show an unredeem button.
            // If not we'll return null so no button shows.
            return hasRedeemedMembers ? false : null;
        }

        if ($scope.currentGroup.itemType === ItemType.TICKET) {
            for (let i = 0; i < $scope.currentGroup.items.length; i++) {
                let orderLine = $scope.currentGroup.items[i];
                if (orderLine.hasIndividualTicketInstances() && !orderLine.isExpired()) {
                    if (orderLine.summaryItemInstanceNetRedemptions() < orderLine.qty) {
                        return true;
                    }
                } else if (orderLine.redeemed < orderLine.qty && !orderLine.isExpired()) {
                    return true;
                }
            }
        }
        return false;
    };

    /**
     * Is this order editable?
     *
     * @return {boolean}
     */
    $scope.canEditOrder = () => {
        if (!$scope.currentOrder.isConfirmed()) {
            return false;
        }

        if ($scope.currentOrder.isCancelled()) {
            // Don't allow editing cancelled orders.
            return false;
        }

        // Allow editing if any of the lines are editable.
        return $scope.currentOrder.items.some(
            // Note this has the potential to return false positives if the item is a ticket with bookable resources,
            // because the item on the line comes from the API orders endpoint and doesn't have the bookable resource
            // type IDs set (if any). But it's not the end of the world. A user can still edit the order but won't be
            // able to edit those order lines. In a way this is better because then the user can still add lines.
            (orderLine) => getOrderLineEditableStatus(orderLine, orderLine.item) === ItemEditableStatus.IS_EDITABLE
        );
    };

    // This has to be after all the functions are defined because otherwise sometimes
    // they can be called before the browser has parsed them.
    let wasBarcodeScan = ($routeParams.wasBarcodeScan === 'true');
    loadOrderByRef($routeParams.bookingRef, wasBarcodeScan);

    $scope.clearRenewingAndViewMembership = function clearRenewingAndViewMembership(membershipRef, currentGroup) {
        // Clear any plans that are renewing - avoids confusion if they start trying to
        // renew *this* one, and then come back.
        currentGroup.clearMembershipRenewing();
        navigationService.viewMembershipDetails(membershipRef, null, true);
    };

    membershipService.showRenewalSuccessIfAppropriate();

    /**
     * @param {Membership} membership
     * @param {MembershipPlan} membershipPlan
     */
    $scope.editMembership = function editMembership(membership, membershipPlan) {
        let dataset = membership.toMembershipDataset();
        membershipManagementService.editDatasets(
            'edit-existing',
            membershipPlan,
            [dataset],
            membership.customerAccount,
            function (modifiedDatasets) {
                if (modifiedDatasets) {
                    membershipService.updateMembership(
                        membership,
                        modifiedDatasets[0]
                    );
                }
            }
        );
    };

    $scope.logVisit = function logVisit(membership, membershipSubscription, membershipPlan) {
        membership.currentRedemptionAction = CurrentRedemptionAction.REDEEMING;
        membershipRedemptionService.logVisit(membership, membershipSubscription, membershipPlan);
    };

    $scope.unlogVisit = function unlogVisit(membership, membershipSubscription, membershipPlan) {
        membership.currentRedemptionAction = CurrentRedemptionAction.UNREDEEMING;
        privilegesManager
            .requirePrivilege(PrivilegeNames.ALLOW_UNREDEEM_MEMBERSHIPS)
            .then(() => {
                membershipRedemptionService.unlogVisit(membership, membershipSubscription, membershipPlan);
            }).catch(() => {
                membership.currentRedemptionAction = CurrentRedemptionAction.NONE;
            });
    };

    /**
     * @param {Membership} membership
     * @param {Member} member
     * @param {number} qty
     */
    const postSingleMemberRedemption = (membership, member, qty) => {
        const name = member.contact.getFullName();
        const messageKey = qty > 0 ? 'MEMBERSHIP_DETAILS.MEMBER_REDEEMED_MSG' : 'MEMBERSHIP_DETAILS.MEMBER_UNREDEEMED_MSG';
        const message = langService.getText(messageKey, { name });

        if (qty > 0) {
            // Show a green success notification for a redemption.
            toastFactory.success('MEMBERSHIP_DETAILS.MEMBER_REDEEMED', message);
        } else {
            // Show a yellow warning notification for an unredemption.
            toastFactory.warning('MEMBERSHIP_DETAILS.MEMBER_UNREDEEMED', message);
        }

        if (
            // If this was a redemption
            member.hasVisitedToday()
            // and this is the only member in the membership
            && membership.members.length === 1
            // and this is the only membership in the order
            && $scope.currentOrder.extractMemberships().length === 1
        ) {
            navigationService.viewRedeem();
        }
    };

    /**
     * Redeem or un-redeem a single member.
     *
     * @param {Member} member
     * @param {MembershipSubscription} membershipSubscription
     * @param {MembershipPlan} membershipPlan
     * @param {Membership} membership
     */
    $scope.toggleMemberVisit = function (
        member,
        membershipSubscription,
        membershipPlan,
        membership
    ) {
        if (
            member.currentRedemptionAction !== CurrentRedemptionAction.NONE
            || membership.currentRedemptionAction !== CurrentRedemptionAction.NONE
        ) {
            return;
        }

        const redeemQty = member.hasVisitedToday() ? -1 : 1;

        let privilegeCheckPromise;
        if (redeemQty < 1) {
            member.currentRedemptionAction = CurrentRedemptionAction.UNREDEEMING;
            privilegeCheckPromise = privilegesManager.requirePrivilege(PrivilegeNames.ALLOW_UNREDEEM_MEMBERSHIPS);
        } else {
            member.currentRedemptionAction = CurrentRedemptionAction.REDEEMING;
            privilegeCheckPromise = Promise.resolve();
        }

        privilegeCheckPromise
            .then(() => {
                membershipRedemptionService.updateMemberVisits(
                    member,
                    membershipSubscription,
                    membershipPlan,
                    redeemQty,
                    () => {
                        postSingleMemberRedemption(membership, member, redeemQty, false);
                    }
                );
            }).catch(() => {
                member.currentRedemptionAction = CurrentRedemptionAction.NONE;
            });
    };

    /**
     * @param {OrderLine} orderLine
     *
     * @return {boolean}
     */
    $scope.redemptionEnabledForOrderLine = (orderLine) => {
        if (!$scope.isOnline && orderLine.ticketHasWaiver) {
            // Disable redemption if we're offline and there may be incomplete waivers.
            return false;
        }

        if ($scope.currentOrderHasIncompleteWaiversForItems([orderLine.itemID])) {
            // Disable redemption if we're online and there are incomplete waivers.
            return false;
        }

        return true;
    };

    /**
     * @return {boolean}
     */
    $scope.currentOrderHasIncompleteWaivers = () => {
        return vuexService.getter('orderDetails/currentOrderHasIncompleteWaivers');
    };

    /**
     * Returns true of any of the waivers on this order apply to *any* of the given itemIDs.
     *
     * @param {number[]} itemIDs
     *
     * @return {boolean}
     */
    $scope.currentOrderHasIncompleteWaiversForItems = (itemIDs) => {
        /**
         * @type {OrderWaiverStatus|null}
         */
        const currentOrderWaiverStatus = vuexService.getStore().state.orderDetails?.currentOrderWaiverStatus;

        if (
            currentOrderWaiverStatus
            && currentOrderWaiverStatus.hasIncompleteWaivers()
            && currentOrderWaiverStatus.containsWaiversForItems(itemIDs)
        ) {
            return true;
        }

        return false;
    };
};

module.exports = OrderDetailCtrl;
